import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslation } from '@monorepo/client-common';
import {
  ContestErrorData,
  extractContestErrorData,
} from '@monorepo/contest-common';
import {
  ContestParticipationDenialReason,
  ContestParticipationMetadata,
  ContestStage,
  ContestWithUserAccount,
} from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';

type TranslationFunction = TFunction<['errors', 'common'], undefined>;

export interface HandlerParams {
  metadata: ContestParticipationMetadata | undefined;
  activeContestName: string | undefined;
  fallbackMessage: string;
  t: TranslationFunction;
}

interface DenialReasonHandler {
  (params: HandlerParams): string;
}

// Re-export for backward compatibility
export { extractContestErrorData };
export type { ContestErrorData };

const handleAlreadySignedUp: DenialReasonHandler = ({
  activeContestName,
  fallbackMessage,
  t,
}) => {
  if (!activeContestName) {
    return fallbackMessage;
  }
  return t('errors:already_signed_up', {
    contestName: activeContestName,
  });
};

const handleInsufficientPurchases: DenialReasonHandler = ({
  metadata,
  fallbackMessage,
  t,
}) => {
  if (!metadata?.requiredPurchases) {
    return fallbackMessage;
  }
  return t('errors:insufficient_purchases', {
    requiredPurchases: metadata.requiredPurchases,
    userPurchases: metadata.userPurchases ?? 0,
  });
};

const handleUserStageNotAllowed: DenialReasonHandler = ({
  metadata,
  fallbackMessage,
  t,
}) => {
  if (!metadata?.requiredBadges) {
    return fallbackMessage;
  }

  const badges = metadata.requiredBadges.join(', ');
  return t('errors:user_stage_not_allowed', {
    requiredBadges: badges,
  });
};

const handleProgramGroupNotAllowed: DenialReasonHandler = ({
  metadata,
  fallbackMessage,
  t,
}) => {
  if (
    !metadata?.requiredProgramGroups ||
    metadata.requiredProgramGroups.length === 0
  ) {
    return fallbackMessage;
  }

  const programs = metadata.requiredProgramGroups.join(' or ');
  return t('errors:program_group_not_allowed', {
    requiredPrograms: programs,
  });
};

const handleCountryNotAllowed: DenialReasonHandler = ({
  metadata,
  fallbackMessage,
  t,
}) => {
  if (!metadata?.allowedCountries) {
    return fallbackMessage;
  }
  return t('errors:country_not_allowed', {
    allowedCountries: metadata.allowedCountries.join(', '),
  });
};

const denialReasonHandlers: Partial<
  Record<ContestParticipationDenialReason, DenialReasonHandler>
> = {
  [ContestParticipationDenialReason.AlreadySignedUp]: handleAlreadySignedUp,
  [ContestParticipationDenialReason.InsufficientPurchases]:
    handleInsufficientPurchases,
  [ContestParticipationDenialReason.UserStageNotAllowed]:
    handleUserStageNotAllowed,
  [ContestParticipationDenialReason.ProgramGroupRequired]:
    handleProgramGroupNotAllowed,
  [ContestParticipationDenialReason.CountryNotAllowed]: handleCountryNotAllowed,
};

/**
 * Translates a denial reason to a user-friendly message
 */
export const translateDenialReason = (
  denialReason: ContestParticipationDenialReason,
  params: HandlerParams,
): string => {
  const handler = denialReasonHandlers[denialReason];

  if (handler) {
    return handler(params);
  }

  const translationKey = `errors:${denialReason}`;
  return (params.t as (key: string) => string)(translationKey);
};

export interface RestrictionReason {
  reason: ContestParticipationDenialReason;
  message: string;
}

/**
 * Hook that returns contest participation eligibility and restriction reasons
 * @param contest - Contest with user account information
 * @returns Object containing participation eligibility and list of denial reasons (each with reason and message)
 */
export const useContestRestrictionReason = (
  contest: ContestWithUserAccount,
): {
  canParticipate: boolean;
  denialReasons: RestrictionReason[];
} => {
  const { t } = useTranslation(['errors', 'common']);
  const {
    dataStore: { contestStore },
  } = useStore();

  const canParticipate = useMemo(
    () => contest.participationEligibility?.canParticipate ?? true,
    [contest.participationEligibility?.canParticipate],
  );

  const reasonsForDenial = useMemo(
    () => contest.participationEligibility?.reasonsForParticipateDenial ?? [],
    [contest.participationEligibility?.reasonsForParticipateDenial],
  );

  const activeContestId = useMemo(
    () => contest.participationEligibility?.sourceId,
    [contest.participationEligibility?.sourceId],
  );

  const activeContest = useMemo(
    () =>
      activeContestId &&
      (contestStore.contestInformation.userContests.find(
        (userContest) => userContest.contestId === activeContestId,
      ) ??
        contestStore.contestInformation.all?.[ContestStage.Active]?.find(
          (active) => active.contestId === activeContestId,
        )),
    [
      activeContestId,
      contestStore.contestInformation.userContests,
      contestStore.contestInformation.all,
    ],
  );

  const activeContestName = useMemo(
    () =>
      activeContest
        ? getDynamicTranslation('name', activeContest, t)
        : undefined,
    [activeContest, t],
  );

  const fallbackMessage = useMemo(
    () => t('errors:registration_not_available'),
    [t],
  );

  const metadata = contest.participationEligibility?.metadata;

  const denialReasons = useMemo((): RestrictionReason[] => {
    const params = {
      metadata,
      activeContestName,
      fallbackMessage,
      t,
    };
    return reasonsForDenial.map((reason) => ({
      reason,
      message: translateDenialReason(reason, params),
    }));
  }, [reasonsForDenial, metadata, activeContestName, fallbackMessage, t]);

  return {
    canParticipate,
    denialReasons,
  };
};

/**
 * Function that extracts error data and returns user-friendly error message
 * Can be used outside of React components (e.g., in catch blocks)
 * @param error - Error object from API call
 * @param activeContestName - Name of the active contest (if applicable)
 * @param fallbackMessage - Fallback message to use if extraction/translation fails
 * @param t - Translation function (from useTranslation hook)
 * @returns Translated error message string
 */
export const getContestErrorReason = (
  error: unknown,
  activeContestName: string | undefined,
  fallbackMessage: string,
  t: TranslationFunction,
): string => {
  const { denialReasons, metadata } = extractContestErrorData(error);

  // Use first denial reason if available
  const denialReason = denialReasons?.[0];

  if (!denialReason) {
    return fallbackMessage;
  }

  return translateDenialReason(denialReason, {
    metadata,
    activeContestName,
    fallbackMessage,
    t,
  });
};
